import { apiClient } from "./apiClient";
import { unwrapApiResponse } from "../shared/utils/apiResponse";

export type OrganizationOption = {
  id: number | string;
  name: string;
  code?: string;
};

export type FinancialYearOption = {
  id: number | string;
  code: string;
  startDate?: string;
  endDate?: string;
};

export async function fetchAccessibleOrganizations() {
  const { data } = await apiClient.get("/organizations/accessible");
  const rows = readArrayPayload(data);
  return rows.map(normalizeOrganization).filter((row): row is OrganizationOption => Boolean(row));
}

export async function fetchFinancialYears(organizationId: string | number) {
  const { data } = await apiClient.get(`/financial-years/${organizationId}`);
  const rows = readArrayPayload(data);
  return rows.map(normalizeFinancialYear).filter((row): row is FinancialYearOption => Boolean(row));
}

function readArrayPayload(payload: unknown): Array<Record<string, unknown>> {
  const unwrapped = unwrapApiResponse<unknown>(payload);
  if (Array.isArray(unwrapped)) return unwrapped as Array<Record<string, unknown>>;
  if (unwrapped && typeof unwrapped === "object") {
    const object = unwrapped as Record<string, unknown>;
    if (Array.isArray(object.data)) return object.data as Array<Record<string, unknown>>;
    if (Array.isArray(object.Data)) return object.Data as Array<Record<string, unknown>>;
    if (Array.isArray(object.items)) return object.items as Array<Record<string, unknown>>;
    if (Array.isArray(object.Items)) return object.Items as Array<Record<string, unknown>>;
  }
  return [];
}

function normalizeOrganization(row: Record<string, unknown>): OrganizationOption | null {
  const id = readValue(row, ["id", "Id", "organizationId", "OrganizationId"]);
  if (id === undefined || id === null || id === "") return null;
  const name = readText(row, ["name", "Name", "organizationName", "OrganizationName", "companyName", "CompanyName", "label", "Label"]) ?? String(id);
  const code = readText(row, ["code", "Code", "organizationCode", "OrganizationCode"]);
  return { id: String(id), name: code && code !== name ? `${name} (${code})` : name, code };
}

function normalizeFinancialYear(row: Record<string, unknown>): FinancialYearOption | null {
  const id = readValue(row, ["id", "Id", "financialYearId", "FinancialYearId"]);
  if (id === undefined || id === null || id === "") return null;
  const code = readText(row, ["code", "Code", "financialYear", "FinancialYear", "year", "Year", "name", "Name", "label", "Label"]) ?? String(id);
  const startDate = readText(row, ["startDate", "StartDate"]);
  const endDate = readText(row, ["endDate", "EndDate"]);
  return { id: String(id), code, startDate, endDate };
}

function readValue(row: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) return row[key];
  }
  return undefined;
}

function readText(row: Record<string, unknown>, keys: string[]) {
  const value = readValue(row, keys);
  return value === undefined || value === null || value === "" ? undefined : String(value);
}
